import React, { useCallback, useMemo } from "react";
import { useI18n } from "../../hooks/i18n-hooks";

import { StackScreenProps } from "@react-navigation/stack";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { useNavigation } from "@react-navigation/native";
import { getWeekNumber } from "../../utils";

import { TimeSheetState } from "../../__generated__/graphql";

import LoadingBoundary from "../../ui_atoms/LoadingBoundary";
import SimplePageContent from "../../components/SimplePageContent";
import { useQuery } from "@apollo/client";
import { GET_TIME_SHEET } from "../../graphql/timesheet";
import Label, { LabelMode } from "../../ui_atoms/Label";
import TimeSheetReview from "../../components/TimeSheetReview";
import { SwipeButton } from "../../components/SwipeButton";
import VStack from "../../ui_atoms/VStack";
import ScreenHeader from "./ScreenHeader";
import Gap from "../../ui_atoms/Gap";
import InfoText from "../../components/InfoText";
import Headline from "../../components/Headline";
import TrackingDatesDisplay from "../../components/TrackingDatesDisplay";
import MissionInfoItem from "../../components/MissionInfoItem";
import { useDateFormatter, useTimesheetsRange } from "../../hooks/date";

type TimesheetReviewProps = StackScreenProps<RootStackParamList, "TimesheetWorkerReview">;

type ScreenParams = StackScreenProps<RootStackParamList>;

const TimesheetWorkerReview = ({ route }: TimesheetReviewProps) => {
    const i18n = useI18n();
    const id = useMemo(() => route.params.id, [route.params.id]);
    const navigation = useNavigation<ScreenParams["navigation"]>();
    const getTimesheetsRange = useTimesheetsRange();

    const { data, error, loading } = useQuery(GET_TIME_SHEET, {
        variables: { timeSheetId: route.params.id || "" },
        skip: !route.params.id,
        fetchPolicy: "network-only",
    });

    const timesheet = data?.getTimeSheet;

    const timesheetId = useMemo(() => id || timesheet?.id, [id, timesheet?.id]);

    const onEditTimesheet = useCallback(() => {
        if (timesheetId) {
            navigation.navigate("TimesheetWorkerInput", { id: timesheetId });
        }
    }, [timesheetId, navigation]);

    const [_, week] = timesheet ? getWeekNumber(new Date(timesheet.fromDate)) : [null, null];
    const isEditEnabled = useMemo(
        () =>
            timesheet?.status === TimeSheetState.Rejected ||
            timesheet?.status === TimeSheetState.Open,
        [timesheet?.status],
    );
    const formatDate = useDateFormatter();

    const statusLabel = useMemo(() => {
        switch (timesheet?.status) {
            case TimeSheetState.Open:
                return {
                    mode: "warning" as LabelMode,
                    text: i18n.t("screens.user.timesheet_review.worker_status.open"),
                };
            case TimeSheetState.Submitted:
                return {
                    mode: "info" as LabelMode,
                    text: i18n.t("screens.user.timesheet_review.worker_status.submitted"),
                };
            case TimeSheetState.Rejected:
                return {
                    mode: "error" as LabelMode,
                    text: i18n.t("screens.user.timesheet_review.worker_status.rejected", {
                        date: formatDate(
                            timesheet?.rejectedAt ? new Date(timesheet.rejectedAt) : new Date(),
                        ),
                    }),
                };
            case TimeSheetState.Accepted:
                return {
                    mode: "success" as LabelMode,
                    text: i18n.t("screens.user.timesheet_review.worker_status.accepted", {
                        date: formatDate(
                            timesheet?.acceptedAt ? new Date(timesheet.acceptedAt) : new Date(),
                        ),
                    }),
                };
            default:
                return {
                    mode: "default" as LabelMode,
                    text: "",
                };
        }
    }, [i18n, timesheet, formatDate]);

    const subLabel = useMemo(() => {
        const missionInfo = timesheet?.missionInfo;
        if (!missionInfo) {
            return i18n.t("loading");
        }
        return i18n.t("time_sheet.nav_sub_title", {
            week,
            range: timesheet ? getTimesheetsRange([timesheet], "long")?.formattedString : "",
        });
    }, [timesheet, week, getTimesheetsRange, i18n]);

    return (
        <>
            <ScreenHeader
                label={i18n.t("time_sheet.nav_title")}
                subLabel={subLabel}
                onLeftAction="back"
                useSmallLabel
            />
            <LoadingBoundary loading={loading && !data} error={error}>
                {timesheet && (
                    <SimplePageContent>
                        <VStack v={1} h={0.5}>
                            <Label {...statusLabel} style={{ textAlign: "center" }} />
                        </VStack>
                        <MissionInfoItem missionInfo={timesheet?.missionInfo} />

                        <Gap gap={0.5} />
                        <VStack h="2/3">
                            <TimeSheetReview
                                hoursBreakdown={timesheet.trackedDurationBreakdown.items}
                                missionMinutesPerWeek={timesheet.contractWeeklyDurationMinutes}
                            />
                            {timesheet.trackingDates.length > 0 && (
                                <>
                                    <Gap gap={1} />
                                    <Headline h="2">
                                        {i18n.t("screens.user.timesheet_review.hours_breakdown")}
                                    </Headline>
                                    <TrackingDatesDisplay trackingDates={timesheet.trackingDates} />
                                </>
                            )}
                        </VStack>
                        <VStack h="2/3" v={1}>
                            {isEditEnabled ? (
                                <SwipeButton
                                    onSwipeSuccess={onEditTimesheet}
                                    title={i18n.t("edit")}
                                    thumbIconName="swipe-left"
                                    enableReverseSwipe
                                />
                            ) : (
                                <>
                                    <Gap gap={1} />
                                    <InfoText
                                        text={i18n.t(
                                            timesheet?.status === TimeSheetState.Accepted
                                                ? "screens.user.timesheet_review.accepted_locked"
                                                : "screens.user.timesheet_review.submitted_locked",
                                        )}
                                    />
                                </>
                            )}
                        </VStack>
                    </SimplePageContent>
                )}
            </LoadingBoundary>
        </>
    );
};

export default TimesheetWorkerReview;
